import React from 'react'
import { useState, useEffect } from 'react'
import moment from 'moment'
import { Fragment } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

const WaitingListSlideOut = ({
  open,
  setOpen,
  notificationData,
  onStatusUpdate,
}) => {
  const navigate = useNavigate()
  const axios = useAxiosPrivate()
  const { setShowLoader, createInfo } = useAuth()
  const [specialismReason, setSpecialismReason] = useState('')
  const [showSpecialismError, setShowSpecialismError] = useState(false)

  if (!notificationData) {
    return null
  }

  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY')
  }

  const viewClient = (clientId, claimId = null, sessionId = null) => {
    if (sessionId) {
      navigate(`/clients/${clientId}/claims/${claimId}/sessions/${sessionId}`)
    } else {
      navigate(`/clients/${clientId}`)
    }
  }

  const revertSpecialisation = async (id) => {
    try {
      setShowLoader(true)
      await axios.get(`/notifications/waiting-list/revert-specialism/${id}`)
      createInfo('success', 'Client has been removed from the Specialism List')

      if (onStatusUpdate) {
        onStatusUpdate()
      }
    } catch (error) {
      console.error('Failed to add to specialism list:', error)
      createInfo('error', 'Could not add to Specialism List')
    } finally {
      setShowLoader(false)
    }
    close()
  }
  const addToSpecialismList = async () => {
    const trimmedReason = specialismReason.trim()

    if (trimmedReason.length < 3) {
      setShowSpecialismError(true)
      return
    }

    try {
      setShowLoader(true)
      await axios.post(
        `/notifications/waiting-list/add-to-specialism/${notificationData.id}`,
        {
          reason: trimmedReason,
        }
      )
      createInfo('success', 'Client has been added to the Specialism List')

      if (onStatusUpdate) {
        onStatusUpdate()
      }
    } catch (error) {
      console.error('Failed to add to specialism list:', error)
      createInfo('error', 'Could not add to Specialism List')
    } finally {
      setShowLoader(false)
    }
    close()
  }

  const showSpecialismSection = !notificationData.meta?.specialism

  const close = () => {
    setShowSpecialismError(false)
    setOpen(false)
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative w-full"
        onClose={setOpen}
        style={{ zIndex: 999 }}
      >
        <Transition
          show={open}
          as={Fragment}
          enter="ease-in-out duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition
                show={open}
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    <div className="bg-blue-500 px-4 py-6 sm:px-6">
                      <div className="flex items-center justify-between">
                        <DialogTitle className="text-base font-semibold leading-6 text-white">
                          Title: {notificationData.title}
                        </DialogTitle>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative rounded-md bg-blue-500 text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => close()}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <FontAwesomeIcon icon={faXmarkCircle} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="mb-3 p-6">
                        <p className="space-y-1">
                          <div className="pb-4">
                            <div className="font-medium">Description: </div>
                            <span className="text-slate-600">
                              {notificationData.body}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 space-y-1">
                            {notificationData.name && (
                              <div>
                                <div>Assigned To:</div>
                                <div>{notificationData.name}</div>
                              </div>
                            )}
                            {notificationData.meta &&
                              Object.entries(notificationData.meta)
                                .filter(
                                  ([key]) =>
                                    key !== 'client_id' && key !== 'status'
                                )
                                .map(([key, value]) => (
                                  <div key={key} className="col-span-1">
                                    <div className="font-medium capitalize">
                                      {key.replace(/_/g, ' ')}:
                                    </div>
                                    <span className="text-slate-600">
                                      {key.includes('date') ||
                                      key.includes('at')
                                        ? formatDate(value)
                                        : typeof value === 'boolean'
                                        ? value
                                          ? 'Yes'
                                          : 'No'
                                        : value}
                                    </span>
                                  </div>
                                ))}
                            <div>
                              <div className="font-medium">Requested By: </div>
                              <span className="text-slate-600">
                                {notificationData.created}
                              </span>
                            </div>
                          </div>
                        </p>
                        {showSpecialismSection && (
                          <div className="mt-6">
                            <label className="font-medium">
                              Add to Specialism List
                            </label>
                            <textarea
                              value={specialismReason}
                              onChange={(e) => {
                                setSpecialismReason(e.target.value)
                                if (e.target.value.trim().length >= 3) {
                                  setShowSpecialismError(false)
                                }
                              }}
                              placeholder="Enter reason..."
                              className={`w-full border rounded-md px-2 py-1 mt-1 ${
                                showSpecialismError
                                  ? 'border-red-500'
                                  : 'border-gray-300'
                              }`}
                            ></textarea>
                            {showSpecialismError && (
                              <p className="text-red-500 text-sm mt-1">
                                Please complete the reason for a specialist.
                              </p>
                            )}
                            <button
                              onClick={addToSpecialismList}
                              className={`rounded-md px-4 py-2 text-white ${
                                specialismReason.trim().length >= 3
                                  ? 'bg-teal-500'
                                  : 'bg-gray-400 cursor-not-allowed'
                              }`}
                              disabled={specialismReason.trim().length < 3}
                            >
                              Add to Specialism List
                            </button>
                          </div>
                        )}
                        <div className="flex items-center  mt-2 w-10/12 absolute bottom-6 space-x-2">
                          {notificationData.meta?.session_id ? (
                            <button
                              onClick={() =>
                                viewClient(
                                  notificationData.meta?.client_id,
                                  notificationData.meta?.claim_id,
                                  notificationData.meta?.session_id
                                )
                              }
                              className="btn mt-4 w-1/2"
                            >
                              <span>View Session</span>
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                viewClient(notificationData.meta?.client_id)
                              }
                              className="btn mt-4 w-1/2"
                            >
                              <span>View Client</span>
                            </button>
                          )}
                          {notificationData.meta?.specialism === true && (
                            <>
                              <button
                                onClick={() =>
                                  revertSpecialisation(notificationData.id)
                                }
                                className="btn primary mt-4 w-1/2"
                              >
                                <span>Revert Specialisation</span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default WaitingListSlideOut
